import React, { useEffect, useRef, useState, useMemo, lazy, Suspense } from 'react'
import {
  IonAccordion,
  IonAccordionGroup,
  IonContent,
  IonItem,
  IonLabel,
  IonIcon,
  useIonViewWillLeave,
} from '@ionic/react'
import { checkmarkSharp } from 'ionicons/icons'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { getStateCustomDisplayDataSelector } from '../selectors/stateCustomDisplayData.selector'
import { ISingleStateCustomElement } from '../store/nemsisCustomData.types'
const GroupContentLazy = lazy(() => import('./GroupContent.component'))

const AccordionList: React.FC = () => {
  const { t } = useTranslation()
  const { stateCustomElements } = useSelector(getStateCustomDisplayDataSelector)

  const accordionGroupRef = useRef<HTMLIonAccordionGroupElement>(null)
  const [openAccordion, setOpenAccordion] = useState<string>('')
  const [loadedDataMap, setLoadedDataMap] = useState<Map<string, ISingleStateCustomElement>>(new Map())

  const memoizedStateCustomElements = useMemo(() => stateCustomElements, [stateCustomElements])

  const stateDataMap = useMemo(
    () => new Map(memoizedStateCustomElements.map((item) => [item.state, item])),
    [stateCustomElements]
  )

  useIonViewWillLeave(() => {
    if (accordionGroupRef.current) {
      accordionGroupRef.current.value = null
    }
    setOpenAccordion('')
    setLoadedDataMap(new Map())
  })

  const handleAccordionClick = (e: React.MouseEvent<HTMLIonItemElement, MouseEvent>, state: string) => {
    e.preventDefault()
    setOpenAccordion((prevState) => {
      const newState = prevState === state ? '' : state
      if (newState && stateDataMap.has(newState)) {
        setLoadedDataMap(new Map().set(newState, stateDataMap.get(newState) ?? new Map()))
      }
      return newState
    })
  }

  useEffect(() => {
    setLoadedDataMap((prevMap) => {
      const newMap = new Map(prevMap)
      for (const [key, value] of stateDataMap) {
        if (newMap.has(key)) {
          newMap.set(key, value)
        }
      }
      return newMap
    })
  }, [stateDataMap])

  const stateLabels = useMemo(
    () =>
      memoizedStateCustomElements.map((state) => ({
        state: state.state,
        stateName: state.stateName,
        isDefaultDataState: state.isDefaultDataState,
        hasData: state?.groups.some((group) =>
          group.customElements.some(
            (element) =>
              element.resultsPNNV ||
              (element?.results && Object.keys(element?.results).length > 0) ||
              element?.results?.length
          )
        ),
      })),
    [memoizedStateCustomElements]
  )

  const stateData = (state: string) => loadedDataMap?.get(state)

  return (
    <IonContent className='bg-white nemsis-data-section'>
      <IonAccordionGroup multiple ref={accordionGroupRef} value={openAccordion}>
        {stateLabels.length > 0 &&
          stateLabels.map((item) => {
            return (
              <IonAccordion key={item.state} value={item.state} className='mb-5'>
                <IonItem slot='header' color='light' onClick={(e) => handleAccordionClick(e, item.state)}>
                  <IonLabel className='fw-700'>
                    <span
                      className={`nemsis-data-required-state ${
                        item.isDefaultDataState ? 'required' : 'not-required'
                      } mr-8`}
                    />
                    {item.stateName}
                    {item.hasData && <IonIcon color='success' className='check-icon' icon={checkmarkSharp} />}
                  </IonLabel>
                </IonItem>
                <div className='ion-padding' slot='content'>
                  <Suspense fallback={<div>Loading...</div>}>
                    <IonLabel color='medium'>
                      {stateData(item?.state)?.groups?.length
                        ? t('customStateElement.SectionDescription')
                        : t('customStateElement.NoCustomData')}
                    </IonLabel>
                    {stateData(item?.state)?.groups?.map((group, index) => (
                      <GroupContentLazy
                        key={group?.groupKey}
                        group={group}
                        item={stateData(item?.state)}
                        index={index}
                      />
                    ))}
                  </Suspense>
                </div>
              </IonAccordion>
            )
          })}
      </IonAccordionGroup>
    </IonContent>
  )
}

export default AccordionList
